import { createSelector } from "reselect";
import { getPartnerMarketing } from "app/reducers/partnerSelector";
import flatten from "lodash/flatten";
import { getLocalizedCountryName } from "app/utils/utils";

const getBrandShops = state => state?.brand?.shops;
const getMarketing = state => getPartnerMarketing(state);
const getDestinationsList = state => state.smartDP.destinations;
const getShopSwitcherList = state => state?.brand?.shopSwitcherList;

export const getCurrentShop = state => state.shop;

export const getTopDestinations = createSelector(
	[getMarketing, getDestinationsList],
	(marketing = {}, destinationsList = []) => {
		const topDestinations = marketing?.topDestinations ?? [];

		const rankByCode = new Map(
			// eslint-disable-next-line radix
			topDestinations.map((destination, rank) => [
				parseInt(destination?._ref?.split(".")[1]),
				rank,
			])
		);

		const seenCodes = new Set();
		const selectedDestinations = destinationsList.filter(destination => {
			const isTopDestination = rankByCode.has(destination.code);
			const isDuplicate = seenCodes.has(destination.code);

			if (!isTopDestination || isDuplicate) {
				return false;
			}

			seenCodes.add(destination.code);
			return true;
		});

		return selectedDestinations.sort(
			(item1, item2) => rankByCode.get(item1.code) - rankByCode.get(item2.code)
		);
	}
);

export const getAllSwitchableShops = createSelector(
	[getBrandShops, getShopSwitcherList],
	(shops = [], shopSwitcherList = []) => {
		const refsSet = shopSwitcherList
			? new Set(shopSwitcherList.map(item => item.code))
			: new Set();

		const filteredShops = refsSet?.size > 0 ? shops.filter(item => refsSet.has(item._id)) : [];

		let switchableShops = flatten(
			filteredShops.map(shop => {
				const locales = shop.locales;

				return locales.map(locale => {
					return {
						shopCode: `${locale}-${shop.sellingCountry}`,
						currencyCode: shop.currencyCode,
						secondaryCurrencies: shop?.secondaryCurrencies,
					};
				});
			})
		);

		return switchableShops.map(({ shopCode, currencyCode, secondaryCurrencies }) => {
			return {
				code: shopCode,
				currencyCode: currencyCode,
				secondaryCurrencies: secondaryCurrencies,
				label: getLocalizedCountryName(shopCode),
			};
		});
	}
);

export const getAllShops = createSelector([getBrandShops], (shops = []) => {
	let allShops = flatten(
		shops.map(shop => {
			const locales = shop.locales;

			return locales.map(locale => {
				return {
					shopCode: `${locale}-${shop.sellingCountry}`,
					currencyCode: shop.currencyCode,
					secondaryCurrencies: shop?.secondaryCurrencies,
				};
			});
		})
	);

	return allShops.map(({ shopCode, currencyCode, secondaryCurrencies }) => {
		return {
			code: shopCode,
			currencyCode: currencyCode,
			secondaryCurrencies: secondaryCurrencies,
			label: getLocalizedCountryName(shopCode),
		};
	});
});
